import productInformation from 'views/ProductPage/addToCartPush/themes/BGH/productInformation.scss';
import { type Price as PriceModel } from 'models/Price';
import Image from 'components/Shared/Image';
import { IWidget, MediaWidget, WidgetType } from 'models/ListViewProduct';
import { useSelector } from 'react-redux';
import {
  getPricePure,
  getProduct,
  getProductHeaderInfo,
  getProductWidgets,
  getQuantity,
  getSelectedAddons
} from 'views/ProductPage/selector';
import { NO_ELEMENT, withCurrentPage } from 'utils';
import { isMobile as isMobileSelector } from 'components/Shared/responsive';
import { rm } from 'utils';
import { getConfigurator, isElfaConfigurator } from 'views/ProductPage/configurators/selector';
import { Price as PriceProps } from 'models/Price';
import { getConfiguratorPrice, getFillings } from 'views/ProductPage/configurators/elfa/selector';
import { IQuantity } from 'models/IQuantity';
import { getAppShell } from 'store/appShell/selector';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { CardPrice } from 'components/Shared/Price/themes/BGH/cardPrice';
import { getIsRentalProduct } from 'components/Shared/Price/selector';

const MEDIA_CUSTOM_IMAGE_DIMENSIONS = {
  width: 150,
  height: 150
};

interface ProductInformationProps {
  hidden: boolean;
  children: JSX.Element;
  amount: number;
}

interface AddonsProps {
  price: PriceProps;
  productId: string;
  quantity?: IQuantity | undefined;
}
interface Quantity extends IQuantity {
  salesUnit?: string;
  factor?: number;
}

interface UseCalculateTotalCost {
  price: PriceProps;
  amount: number;
  quantity: Quantity;
  configurator: ConfiguratorBase;
  currencyUnit: string;
}

const isMediaWidget = (widget: IWidget): widget is MediaWidget => widget.type === WidgetType.Media;

const ProductWidget = ({ ...widget }: IWidget) => {
  if (isMediaWidget(widget) && widget.media) {
    return (
      <Image
        className={productInformation.mediaImage}
        {...widget.media[0]}
        {...MEDIA_CUSTOM_IMAGE_DIMENSIONS}
      />
    );
  }

  return NO_ELEMENT;
};

const getFormattedProductsCount = (products: number, quantity: Quantity): string => {
  if (quantity.salesUnit) {
    return `${products} ${quantity.salesUnit}`;
  }

  return `${products} ${quantity.unit}`;
};

const getListPrice = ({
  configurator,
  price,
  quantity
}: Pick<UseCalculateTotalCost, 'configurator' | 'price' | 'quantity'>): number => {
  const { salesQuantity, salesUnit, factor } = quantity;
  const { lowestPossiblePrice, listPrice } = price;
  const isRentalProduct = getIsRentalProduct(price);

  if (isRentalProduct) return price.rentalPrice;

  if (isElfaConfigurator(configurator) && lowestPossiblePrice) {
    return lowestPossiblePrice;
  } else if (salesQuantity && salesUnit && factor) {
    return factor * listPrice;
  }

  return listPrice;
};

const getSumOfSelectedAddons = (selectedAddons: AddonsProps[]) => {
  return selectedAddons.map(addon => addon.price.listPrice).reduce((prev, curr) => prev + curr, 0);
};

const useCalculateTotalCost = ({
  configurator,
  amount,
  price,
  quantity,
  currencyUnit
}: UseCalculateTotalCost): PriceModel => {
  const selectedAddons = useSelector(withCurrentPage(getSelectedAddons)) as AddonsProps[];
  const totalCost = getSumOfSelectedAddons(selectedAddons);
  const listPrice = getListPrice({ configurator, price, quantity });
  const fillingPrice = isElfaConfigurator(configurator) ? getConfiguratorPrice(configurator) : 0;
  const priceSum = totalCost + listPrice + fillingPrice;
  const presentationPrice = priceSum * amount;
  const isRentalProduct = getIsRentalProduct(price);
  return {
    ...price,
    currencyUnit: isRentalProduct ? price.currencyUnit : ` ${currencyUnit}`,
    rentalPrice: isRentalProduct ? presentationPrice : 0,
    presentationPrice
  };
};

const hasFillings = (configurator: ConfiguratorBase): boolean => {
  if (isElfaConfigurator(configurator)) {
    const hasFillings = getFillings(configurator.values).length;

    return hasFillings > 0;
  }
  return false;
};

export const ProductInformation = ({ hidden, children, amount }: ProductInformationProps) => {
  const appShell = useSelector(getAppShell);
  const product = useSelector(withCurrentPage(getProduct));
  const productHeaderInfo = useSelector(withCurrentPage(getProductHeaderInfo));
  const configurator = useSelector(withCurrentPage(getConfigurator));
  const isMobile = useSelector(isMobileSelector);
  const widgets = useSelector(withCurrentPage(getProductWidgets));
  const quantity = useSelector(withCurrentPage(getQuantity)) as Quantity;
  const price = getPricePure({ product, configurator });
  const numberOfSelectedAddons = useSelector(withCurrentPage(getSelectedAddons)).length;

  const currencyUnit = appShell.currencySymbol;
  const variantName = productHeaderInfo.variantName;
  const category = productHeaderInfo.title.slice(productHeaderInfo.titleSplitPos);

  const formattedAddonsStr = ` + ${rm('Cart_subitems_header').toLowerCase()}`;
  const formattedProductCountStr = getFormattedProductsCount(amount, quantity);

  const formattedPrice = useCalculateTotalCost({
    price,
    amount,
    quantity,
    configurator,
    currencyUnit
  });

  const hasSelectedAddons = numberOfSelectedAddons > 0;
  const hasSelectedFillings = hasFillings(configurator);

  const shouldShowAddonsOrFillings = hasSelectedAddons || hasSelectedFillings;
  const formattedCaptions = formattedAddonsStr;

  const shouldShowProductsCount = amount > 1 || shouldShowAddonsOrFillings;

  const setProductClassName =
    (shouldShowProductsCount && isMobile) || !isMobile
      ? productInformation.product
      : productInformation.productsCount;

  return (
    <>
      {!hidden && (
        <div className={productInformation.container}>
          {!isMobile && widgets?.map(widget => <ProductWidget key={widget.type} {...widget} />)}

          <div className={setProductClassName}>
            <h3 className={productInformation.title}>
              {category ? category : productHeaderInfo.title}
            </h3>
            <span className={productInformation.variant}>{variantName}</span>
          </div>

          {shouldShowProductsCount && (
            <div className={productInformation.productsCount}>
              <span className={productInformation.countText}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: formattedProductCountStr
                  }}
                />
                {shouldShowAddonsOrFillings ? formattedCaptions : null}
              </span>
            </div>
          )}

          <CardPrice price={formattedPrice} page="addToCartPush" />

          <div className={productInformation.button}>{children}</div>
        </div>
      )}
    </>
  );
};
